import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ShieldCheckIcon,
  CalendarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const HealthCheck = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHealthChecks();
  }, []);

  const fetchHealthChecks = async () => {
    try {
      setLoading(true);

      // ダミーデータ（実際のAPIが実装されるまで）
      // Setting to empty for production readiness
      setHealthChecks([]);
    } catch (error) {
      console.error('Failed to fetch health checks:', error);
      toast.error('検便履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const isOverdue = due < today && status === 'pending';

    switch (status) {
      case 'completed':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">完了</span>;
      case 'pending':
        if (isOverdue) {
          return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">期限切れ</span>;
        }
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">検査中</span>;
      case 'requested':
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">依頼済み</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">不明</span>;
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'negative':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">陰性</span>;
      case 'positive':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">陽性</span>;
      default:
        return null;
    }
  };

  const downloadReport = async (reportUrl) => {
    try {
      // レポートダウンロード処理（実装待ち）
      toast.success('検査結果をダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">検便依頼と結果</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 検便検査について */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ShieldCheckIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-blue-900 mb-2">検便検査について</h2>
              <p className="text-blue-800 text-sm leading-relaxed mb-3">
                飲食店での勤務では、食品衛生法に基づき検便検査が必要な場合があります。
                バイターズでは、必要に応じて事業者から検便検査の依頼が届きます。
              </p>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> 検査費用は事業者が負担します</li>
                <li> 検査結果は事業者と共有されます</li>
                <li> 陽性の場合は勤務開始前に治療が必要です</li>
                <li> 検査は指定の医療機関で実施してください</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 検便履歴 */}
        <div className="space-y-4">
          {healthChecks.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <ShieldCheckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">検便依頼はありません</h3>
              <p className="text-gray-600">
                飲食店での勤務が決まると、必要に応じて検便検査の依頼が届きます。
              </p>
            </div>
          ) : (
            healthChecks.map((healthCheck) => (
              <div key={healthCheck.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        {healthCheck.type_name}
                      </h3>
                      {getStatusBadge(healthCheck.status, healthCheck.due_date)}
                      {healthCheck.result && (
                        <span className="ml-2">
                          {getResultBadge(healthCheck.result)}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 mb-3">依頼元: {healthCheck.employer_name}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center text-gray-600">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        依頼日: {formatDate(healthCheck.requested_date)}
                      </div>

                      <div className="flex items-center text-gray-600">
                        <ClockIcon className="w-4 h-4 mr-2" />
                        期限: {formatDate(healthCheck.due_date)}
                      </div>

                      <div className="flex items-center text-gray-600">
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        検査機関: {healthCheck.lab_name}
                      </div>
                    </div>

                    {/* 検査項目 */}
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">検査項目:</h4>
                      <div className="flex flex-wrap gap-2">
                        {healthCheck.test_items.map((item, index) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* ステータスごとの詳細情報 */}
                    {healthCheck.status === 'pending' && (
                      <div className="mt-4 bg-yellow-50 p-3 rounded-lg">
                        <div className="flex items-center text-yellow-800">
                          <ClockIcon className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">検査結果待ち</span>
                        </div>
                        <p className="text-yellow-700 text-sm mt-1">
                          検査機関での結果確認をお待ちください。
                        </p>
                      </div>
                    )}

                    {healthCheck.status === 'completed' && healthCheck.result === 'negative' && (
                      <div className="mt-4 bg-green-50 p-3 rounded-lg">
                        <div className="flex items-center text-green-800">
                          <CheckCircleIcon className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">検査完了 - 陰性</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">
                          検査結果は陰性でした。勤務に問題ありません。
                        </p>
                      </div>
                    )}

                    {healthCheck.status === 'completed' && healthCheck.result === 'positive' && (
                      <div className="mt-4 bg-red-50 p-3 rounded-lg">
                        <div className="flex items-center text-red-800">
                          <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">検査完了 - 陽性</span>
                        </div>
                        <p className="text-red-700 text-sm mt-1">
                          検査結果が陽性のため、治療後の再検査が必要です。事業者にご相談ください。
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ダウンロードボタン */}
                  {healthCheck.status === 'completed' && healthCheck.report_url && (
                    <button
                      onClick={() => downloadReport(healthCheck.report_url)}
                      className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      結果DL
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* 注意事項 */}
        <div className="mt-8 bg-yellow-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-900 mb-2">重要な注意事項</h4>
              <ul className="text-yellow-800 text-sm space-y-1">
                <li> 検便検査は指定された期限内に必ず実施してください</li>
                <li> 陽性結果が出た場合は、医師の診断を受けてください</li>
                <li> 検査結果は事業者と共有されます</li>
                <li> 検査費用の支払いについては事業者にご確認ください</li>
                <li> 検査に関する質問は事業者または検査機関にお問い合わせください</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;